import { useState, useEffect } from "react";
import "./../auth/index.scss";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { AdminPanelLogin, checkBackendPublic } from ".";
import { setStoredAdminUser, setAuthToken } from "../../utils/auth";
import { establishSession } from "../../services/tabSession";
import { useAuth } from "../../context/AuthContext";
import logo from "./../../assets/images/logo.png";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login: markAuthenticated, isAuthenticated } = useAuth();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [apiCheck, setApiCheck] = useState({ status: "idle" });

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (location.state?.sessionExpired) {
      toast.info(
        location.state.message ||
          "Your session has expired due to inactivity. Please log in again.",
        { toastId: "session-expired" }
      );
      window.history.replaceState({}, document.title);
    } else if (location.state?.logoutMessage) {
      toast.success(location.state.logoutMessage, { toastId: "manual-logout" });
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    let cancelled = false;
    checkBackendPublic().then((r) => {
      if (cancelled) return;
      setApiCheck(
        r.ok
          ? { status: "ok", url: r.url }
          : { status: "fail", url: r.url, statusCode: r.status, message: r.message }
      );
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (event) => {
    event.preventDefault();
    setError(null);

    if (!identifier.trim() || !password) {
      setError("Please enter your email / username and password.");
      return;
    }

    setLoading(true);
    try {
      const response = await AdminPanelLogin({ identifier: identifier.trim(), password });

      if (response.status === 200) {
        const { token, user } = response.data.data;

        if (!["Admin", "Editor"].includes(user.role)) {
          setError("You are not authorized to access the admin panel.");
          setLoading(false);
          return;
        }

        setAuthToken(token);
        setStoredAdminUser(user);
        establishSession();
        markAuthenticated();
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message;

      if (status === 404 || msg === "User does not exist.") {
        setError("User does not exist.");
      } else if (status === 401 || msg === "Invalid credentials.") {
        setError("Invalid credentials.");
      } else if (status === 403) {
        setError("Your account is inactive. Please contact the administrator.");
      } else {
        setError(msg || "Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="h-screen bg-gradient-to-br from-stone-50 to-stone-100 justify-center flex items-center flex-col gap-3 p-4">
      {process.env.REACT_APP_ENV === "development" && apiCheck.status === "fail" && (
        <div
          className="max-w-md rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-center text-xs text-amber-900"
          role="status"
        >
          API check failed for <span className="font-mono">{apiCheck.url}</span>
          {apiCheck.statusCode != null ? ` (HTTP ${apiCheck.statusCode})` : ""}. Admin login may
          still work if only verify paths differ.
        </div>
      )}
      {process.env.REACT_APP_ENV === "development" && apiCheck.status === "ok" && (
        <div className="max-w-md text-center text-xs text-stone-500" role="status">
          API reachable at <span className="font-mono">{apiCheck.url}</span>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-lg border border-stone-200/80 w-full max-w-md items-center flex flex-col py-12 login-box">
        <div className="pb-8">
          <img className="h-14 w-auto mx-auto" src={logo} alt="logo" />
        </div>
        <h1 className="text-stone-900 text-2xl font-semibold tracking-tight">
          KisanGrow Admin Panel
        </h1>
        <p className="text-stone-500 text-sm mt-2 mb-8">Sign in to your account to continue</p>

        <form onSubmit={login} className="w-full px-10 flex flex-col gap-5">
          <div>
            <label className="block text-sm font-medium leading-6 text-stone-700 mb-2">
              Email Address or Username
            </label>
            <input
              id="admin-login-identifier"
              placeholder="admin@kisangrow.com or kisangrow-admin"
              className="block w-full rounded-xl border-0 py-2.5 text-stone-900 shadow-sm ring-1 ring-inset ring-stone-300 placeholder:text-stone-400 focus:ring-2 focus:ring-inset focus:ring-stone-900 sm:text-sm sm:leading-6 transition-shadow px-4"
              type="text"
              autoFocus
              autoComplete="username"
              value={identifier}
              onChange={(e) => {
                setIdentifier(e.target.value);
                setError(null);
              }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium leading-6 text-stone-700 mb-2">
              Password
            </label>
            <input
              id="admin-login-password"
              placeholder="Enter your password"
              className="block w-full rounded-xl border-0 py-2.5 text-stone-900 shadow-sm ring-1 ring-inset ring-stone-300 placeholder:text-stone-400 focus:ring-2 focus:ring-inset focus:ring-stone-900 sm:text-sm sm:leading-6 transition-shadow px-4"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError(null);
              }}
            />
          </div>

          <button
            id="admin-login-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-stone-900 text-white hover:bg-stone-800 disabled:bg-stone-400 h-11 mt-2 text-sm font-medium rounded-xl transition-colors"
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>

        {error && (
          <span className="text-red-600 text-sm mt-6 px-10 text-center">{error}</span>
        )}
      </div>
    </main>
  );
}
